import { forwardRef, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import { SettingIcon, UserManagementIcon } from "@/assets/icons";
import { UnlockPremiumModal } from "@/components/dataset/datasetExplorer/UnlockPremiumModal";
import Tooltip from "@/components/ui/tooltip";
import { MESSAGE } from "@/constants/message";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useIsModuleMenuVisible } from "@/hooks/permission/useIsModuleInPlan";
import { clearCurrRunningAnalysisId } from "@/store/slice/spatialSlice";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import { PopoverMenuProps, SidebarFooterPopoverType } from "../types";
import FooterItemWithPopover from "./FooterItemWithPopover";

const ICON_CLASSNAME = "w-5 h-5 fill-dim-gray";
const SETTING_MENU_ITEM_CLASSNAME =
  "w-full justify-start gap-2 border-0 hover:bg-neutral-100 p-2 rounded-xl";

type SettingMenuWrapperProps = {
  setActivePopover: (popoverType: SidebarFooterPopoverType) => void;
  activePopover?: SidebarFooterPopoverType;
  isPopoverTriggerOrigin: (e: Event) => boolean;
  sidebarMenu?: boolean;
};

export const SettingMenuWrapper = forwardRef<
  HTMLButtonElement,
  SettingMenuWrapperProps
>(
  (
    { activePopover, isPopoverTriggerOrigin, setActivePopover, sidebarMenu },
    ref
  ) => {
    // Lives here, not in SettingMenu, because the popover content unmounts on close
    const [isUserManagementUnlockOpen, setIsUserManagementUnlockOpen] =
      useState(false);

    return (
      <>
        <FooterItemWithPopover
          togglePopover={(open) => {
            setActivePopover(open ? "setting" : "");
          }}
          classNamePopoverContent={twMerge("mt-3", sidebarMenu && "ml-6")}
          isPopoverTriggerOrigin={isPopoverTriggerOrigin}
          popoverTrigger={
            <Button
              variant="secondary"
              data-testid="setting-popover-trigger"
              ref={ref}
              className={twMerge(
                " p-0 rounded-lg hover:bg-teal-50 border-0",
                activePopover === "setting" && "bg-teal-50",
                sidebarMenu && "w-[231px] justify-start"
              )}
            >
              <Tooltip
                disable={sidebarMenu}
                content="Settings"
                position="right"
                key="Settings"
              >
                <div className="flex items-center justify-center">
                  <SettingIcon
                    className={twMerge(
                      "fill-dim-gray hover:fill-teal-500 w-10 h-10 p-2",
                      activePopover === "setting" && "fill-teal-500"
                    )}
                  />
                  <span
                    className={twMerge(
                      "whitespace-pre text-sm font-medium",
                      !sidebarMenu &&
                        "opacity-0 translate-x-28 overflow-hidden hidden",
                      activePopover === "setting"
                        ? "text-black font-bold"
                        : "text-dim-gray"
                    )}
                  >
                    Settings
                  </span>
                </div>
              </Tooltip>
            </Button>
          }
          popoverContent={
            <SettingMenu
              setActivePopover={setActivePopover}
              onUserManagementUnavailable={() =>
                setIsUserManagementUnlockOpen(true)
              }
            />
          }
          isOpen={activePopover === "setting"}
        />
        {isUserManagementUnlockOpen && (
          <UnlockPremiumModal
            title={MESSAGE.UNLOCK_USER_MANAGEMENT_TITLE}
            contactUsDesc={MESSAGE.UNLOCK_USER_MANAGEMENT_CONTENT}
            onClose={() => setIsUserManagementUnlockOpen(false)}
          />
        )}
      </>
    );
  }
);

const SettingMenuItem = ({
  onClick,
  href,
  label,
  icon,
  isLocked = false
}: {
  onClick: () => void;
  href: string;
  label: string;
  icon: React.ReactNode;
  isLocked?: boolean;
}) => {
  const handleClick = () => {
    onClick();
    toast.dismiss();
  };
  const content = (
    <div className="flex flex-1 gap-2">
      {icon}
      <div className="flex flex-col">
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );

  // A locked module opens its Contact Sales modal, so there is no link to follow
  if (isLocked) {
    return (
      <Button
        className={SETTING_MENU_ITEM_CLASSNAME}
        variant="secondary"
        onClick={handleClick}
        data-testid={`setting-menu-${label}`}
      >
        {content}
      </Button>
    );
  }

  return (
    <Link to={href} onClick={handleClick} data-testid={`setting-menu-${label}`}>
      <Button className={SETTING_MENU_ITEM_CLASSNAME} variant="secondary">
        {content}
      </Button>
    </Link>
  );
};

type SettingMenuProps = PopoverMenuProps & {
  onUserManagementUnavailable: () => void;
};

const SettingMenu = ({
  setActivePopover,
  onUserManagementUnavailable
}: SettingMenuProps) => {
  const dispatch = useDispatch();
  const userPermissions = useGetUserPermission();
  const {
    isVisible: isUserManagementGranted,
    isInPlan: isUserManagementInPlan
  } = useIsModuleMenuVisible(EModuleName.USER_MANAGEMENT);
  const isUserGroupManagementGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][EModulePermission.VIEW];

  const handleOnClick = () => {
    setActivePopover("");
    dispatch(clearCurrRunningAnalysisId());
  };

  return (
    <div className="flex flex-col gap-3 p-2">
      <div className="flex flex-col">
        {isUserManagementGranted && (
          <SettingMenuItem
            href="/user-management"
            isLocked={!isUserManagementInPlan}
            onClick={
              isUserManagementInPlan
                ? handleOnClick
                : () => {
                    // Stay on the page, so the running analysis must not be cleared
                    setActivePopover("");
                    onUserManagementUnavailable();
                  }
            }
            label="Users"
            icon={<UserManagementIcon className={ICON_CLASSNAME} />}
          />
        )}
        {isUserGroupManagementGranted && (
          <SettingMenuItem
            href="/group-management"
            onClick={handleOnClick}
            label="Group"
            icon={<UserManagementIcon className={ICON_CLASSNAME} />}
          />
        )}
      </div>
    </div>
  );
};

export default SettingMenuWrapper;
